/**
 * Phase 8 T-25 §29.B — Local paraphrase stub.
 *
 * Produces a `ParaphraseTurn`-ready string from a user prompt by extracting
 * a handful of common keywords and wrapping them in `**bold**` markdown so
 * the UI highlights them via `<mark class="ggj-keyword">`.
 *
 * C-03 — The confidence score is now env-gated:
 *   VITE_SMART_MATCH_CONFIDENCE_SOURCE='stub' (default) → constant fallback.
 *   VITE_SMART_MATCH_CONFIDENCE_SOURCE='api'            → orchestratorService.
 * Any api error silently falls back to the stub so Stage 1 always renders.
 */

import type { ClarificationRound } from '../components/ClarificationStage';
import type {
  SmartMatchJdEntry,
  SmartMatchResumeEntry,
} from './smartMatchThreads';
import { orchestratorService } from '@core/supabase/services/orchestratorService';
import { invokeLlamaChat, type LlamaChatMessage } from '@core/ai/llamaChat';

/** Fallback confidence — also the stub return. Kept as the single literal. */
const FALLBACK_CONFIDENCE = 0.7;

/**
 * Synchronous fallback. Used when `VITE_SMART_MATCH_CONFIDENCE_SOURCE` is
 * unset / `'stub'`, or when the orchestrator call throws.
 */
export function stubParaphraseConfidence(): number {
  return FALLBACK_CONFIDENCE;
}

type ConfidenceSource = 'stub' | 'api';

function confidenceSource(): ConfidenceSource {
  return import.meta.env.VITE_SMART_MATCH_CONFIDENCE_SOURCE === 'api' ? 'api' : 'stub';
}

/**
 * Resolve the paraphrase confidence for `threadId`. Returns a value in [0,1].
 *
 * In 'api' mode we call `orchestratorService.getClarifyConfidence`; any
 * error — or a null/undefined response — falls back to the stub so Stage 1
 * never blocks on orchestrator downtime.
 */
export async function getParaphraseConfidence(threadId: string): Promise<number> {
  if (confidenceSource() !== 'api') return stubParaphraseConfidence();
  try {
    const v = await orchestratorService.getClarifyConfidence(threadId);
    if (typeof v !== 'number' || !Number.isFinite(v)) return stubParaphraseConfidence();
    return Math.min(1, Math.max(0, v));
  } catch (err) {
    console.warn(
      '[paraphraseStub] orchestratorService.getClarifyConfidence failed; using fallback',
      err,
    );
    return stubParaphraseConfidence();
  }
}

/**
 * Extract up to 5 candidate keywords from a prompt that we can confidently
 * bold as "filter signals". Deliberately conservative — false positives
 * undermine the user's trust in the paraphrase.
 */
export function extractKeywords(prompt: string): string[] {
  const bag: string[] = [];
  const seen = new Set<string>();
  const push = (k: string) => {
    const key = k.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    bag.push(k);
  };

  // Years-of-experience phrases.
  const yearsRe = /\b(\d+\+?\s*(?:to\s*\d+\+?\s*)?(?:years?|yrs?))\s+(?:of\s+)?(?:experience|exp)?/gi;
  let m: RegExpExecArray | null;
  while ((m = yearsRe.exec(prompt)) !== null) {
    push(m[1]!.trim());
  }

  // Common tech / work-style / market tokens.
  const dictionary = [
    'Figma', 'React', 'Node', 'Node.js', 'Python', 'Java', 'Go',
    'TypeScript', 'JavaScript', 'Kubernetes', 'AWS', 'GCP', 'Azure',
    'SaaS', 'B2B', 'B2C', 'Remote', 'Hybrid', 'On-site',
    'Senior', 'Staff', 'Principal', 'Lead', 'Manager', 'IC',
    'Product Manager', 'Product Designer', 'UX', 'UI',
    'backend', 'frontend', 'full-stack', 'fullstack',
    'US', 'UK', 'EU', 'India', 'Bangalore', 'Bengaluru', 'Mumbai', 'Delhi',
    'startup', 'fintech', 'healthtech', 'edtech',
  ];
  for (const kw of dictionary) {
    const re = new RegExp(`\\b${kw.replace(/[.+]/g, '\\$&')}\\b`, 'i');
    if (re.test(prompt)) push(kw);
    if (bag.length >= 5) break;
  }

  return bag.slice(0, 5);
}

/**
 * Returns true when the prompt is specific enough that we can start
 * sourcing immediately without asking a clarifying question.
 *
 * Heuristic: prompt is "specific" if it's longer than 80 characters OR
 * contains at least 2 keyword signals (skills, years, locations, etc.).
 * Short/vague prompts (< 40 chars, no signals) still get a single question
 * so the search plan isn't empty.
 */
function promptIsSpecific(prompt: string, keywords: string[]): boolean {
  if (prompt.trim().length >= 80) return true;
  if (keywords.length >= 2) return true;
  return false;
}

/**
 * Build the initial (round 0) paraphrase turn.
 *
 * Shape:
 *   paragraph 1 — paraphrase summary with **bolded** filter keywords.
 *   paragraph 2 — a SINGLE clarifying question ONLY when the brief is
 *                  too vague to start searching. Specific briefs skip the
 *                  question and go straight to "Press Start looking".
 */
export function buildInitialParaphrase(prompt: string): ClarificationRound {
  const keywords = extractKeywords(prompt);
  const boldList =
    keywords.length >= 2
      ? `${keywords.slice(0, -1).map((k) => `**${k}**`).join(', ')} and **${keywords.at(-1)}**`
      : keywords.length === 1
        ? `**${keywords[0]}**`
        : '';

  const summary = boldList
    ? `Got it — I'll search for candidates matching ${boldList}. Your full brief is captured.`
    : "Got it — I've captured your brief and I'm ready to search.";

  if (promptIsSpecific(prompt, keywords)) {
    return {
      paraphrase: summary,
      thoughtMs: 800 + Math.floor(Math.random() * 600),
    };
  }

  const question =
    "Any specific must-haves (location, seniority, key skills) to add before I start?";

  return {
    paraphrase: `${summary}\n\n${question}`,
    thoughtMs: 1000 + Math.floor(Math.random() * 800),
  };
}

/**
 * Build a follow-up paraphrase after the user sends a refinement. We keep
 * the previously-extracted keywords and add any new ones the user called
 * out in their refinement. The question becomes more specific.
 */
export function buildFollowUpParaphrase(
  originalPrompt: string,
  refinement: string,
  round: number,
): ClarificationRound {
  const mergedKeywords = extractKeywords(`${originalPrompt}\n${refinement}`);
  const boldList =
    mergedKeywords.length >= 2
      ? `${mergedKeywords.slice(0, -1).map((k) => `**${k}**`).join(', ')} and **${mergedKeywords.at(-1)}**`
      : mergedKeywords.length === 1
        ? `**${mergedKeywords[0]}**`
        : '';

  const summary = boldList
    ? `Updated — I'm now filtering for ${boldList}. Your refinement is captured.`
    : `Updated — your refinement is captured.`;

  // Always suppress further questions after the first refinement — one
  // clarification round is enough. Just confirm and get out of the way.
  const suppressQuestion = round >= 1;
  if (suppressQuestion) {
    return {
      paraphrase: summary,
      thoughtMs: 700 + Math.floor(Math.random() * 400),
    };
  }

  const question = "Any dealbreakers I should filter out before I start?";

  return {
    paraphrase: `${summary}\n\n${question}`,
    thoughtMs: 900 + Math.floor(Math.random() * 900),
  };
}

// ── §29.E Multi-JD paraphrase builders ──────────────────────────────────────

/**
 * Intersect keyword sets across N JDs. Returns `{ common, differences }`
 * where `common` is shared by ≥ 2 JDs and `differences` is the per-JD
 * diff vs the common set. Conservative: uses the same keyword dictionary
 * as single-JD so false positives are minimised.
 */
export function summariseMultiJd(jds: SmartMatchJdEntry[]): {
  common: string[];
  perJd: Array<{ id: string; title: string; only: string[]; all: string[] }>;
} {
  const perJd = jds.map((j) => ({
    id: j.id,
    title: j.title,
    all: extractKeywords(j.text),
    only: [] as string[],
  }));
  if (perJd.length === 0) return { common: [], perJd: [] };
  // Intersection across all JDs.
  const firstSet = new Set(perJd[0]!.all.map((k) => k.toLowerCase()));
  for (const entry of perJd.slice(1)) {
    const set = new Set(entry.all.map((k) => k.toLowerCase()));
    for (const key of Array.from(firstSet)) {
      if (!set.has(key)) firstSet.delete(key);
    }
  }
  const commonLower = firstSet;
  // Keep original casing from the first JD that had the keyword.
  const common: string[] = [];
  for (const entry of perJd) {
    for (const kw of entry.all) {
      if (commonLower.has(kw.toLowerCase()) && !common.some((c) => c.toLowerCase() === kw.toLowerCase())) {
        common.push(kw);
      }
    }
  }
  // Compute per-JD only (kw not in common).
  for (const entry of perJd) {
    entry.only = entry.all.filter((kw) => !commonLower.has(kw.toLowerCase()));
  }
  return { common, perJd };
}

/**
 * §29.E agentic mode — single combined paraphrase across all JDs.
 *   "All three JDs share **Figma** + **3+ years experience**. JD-2 and
 *   JD-3 require **management exposure**; JD-1 is **IC-only**."
 */
export function buildMultiJdCombinedParaphrase(
  jds: SmartMatchJdEntry[],
): ClarificationRound {
  if (jds.length === 0) {
    return {
      paraphrase: 'No JDs to paraphrase yet. Add at least one and press Send.',
      thoughtMs: 600,
    };
  }
  const { common, perJd } = summariseMultiJd(jds);
  const countWord =
    jds.length === 2 ? 'Both' : jds.length === 3 ? 'All three' : `All ${jds.length}`;
  const commonBold =
    common.length >= 2
      ? `${common.slice(0, -1).map((k) => `**${k}**`).join(', ')} and **${common.at(-1)}**`
      : common.length === 1
        ? `**${common[0]}**`
        : null;
  const commonSentence = commonBold
    ? `${countWord} JDs share ${commonBold}.`
    : `${countWord} JDs look fairly distinct — there's no single keyword they all share.`;

  const diffPieces: string[] = [];
  for (const entry of perJd) {
    if (entry.only.length === 0) continue;
    const piece =
      entry.only.length >= 2
        ? `${entry.title} calls out ${entry.only
            .slice(0, 3)
            .map((k) => `**${k}**`)
            .join(', ')}`
        : `${entry.title} calls out **${entry.only[0]}**`;
    diffPieces.push(piece);
  }
  const diffSentence =
    diffPieces.length > 0 ? ` ${diffPieces.join('; ')}.` : '';

  const question =
    perJd.some((e) => /manage|lead|staff|principal/i.test(e.only.join(' ')))
      ? 'Should I prioritise IC-leaning candidates, or keep all three roles equal?'
      : 'Should I score candidates against the union of these JDs, or keep them ranked per-JD?';

  return {
    paraphrase: `${commonSentence}${diffSentence}\n\n${question}`,
    thoughtMs: 1600 + Math.floor(Math.random() * 1400),
  };
}

/**
 * §29.E manual mode — per-JD paraphrase, one `<ParaphraseTurn>` each.
 * Question is individual (reuses the initial-single pattern).
 */
export function buildMultiJdPerJdParaphrases(
  jds: SmartMatchJdEntry[],
): Array<{ jdId: string; title: string; round: ClarificationRound }> {
  return jds.map((j) => ({
    jdId: j.id,
    title: j.title,
    round: buildInitialParaphrase(j.text),
  }));
}

// ── §29.F Bulk-resumes paraphrase builders ─────────────────────────────────

/**
 * Heuristic seniority bucketing from filename / metadata. The real
 * signal comes from the resume-parse service; until that's live we
 * split evenly for a plausible paraphrase.
 *
 * TODO(§9): wire real parse signals once the inference service lands.
 */
function stubSeniorityDistribution(resumes: SmartMatchResumeEntry[]): {
  senior: number;
  mid: number;
  junior: number;
  unparseable: number;
} {
  const parsed = resumes.filter((r) => r.parseStatus === 'parsed');
  const unparseable = resumes.length - parsed.length;
  const senior = Math.max(1, Math.floor(parsed.length * 0.3));
  const junior = Math.max(0, Math.floor(parsed.length * 0.25));
  const mid = Math.max(0, parsed.length - senior - junior);
  return { senior, mid, junior, unparseable };
}

/**
 * §29.F agentic mode — seniority summary + filter-threshold question.
 */
export function buildBulkResumeParaphrase(
  resumes: SmartMatchResumeEntry[],
  contextJd: string,
): ClarificationRound {
  if (resumes.length === 0) {
    return {
      paraphrase: "No résumés uploaded yet — add some files and I'll parse them.",
      thoughtMs: 600,
    };
  }
  const dist = stubSeniorityDistribution(resumes);
  const keywords = contextJd ? extractKeywords(contextJd) : [];
  const signalKw = keywords[0];

  const countSentence = `I parsed **${resumes.length - dist.unparseable}** résumés.${
    dist.unparseable > 0 ? ` **${dist.unparseable}** couldn't be parsed and will land in the Unparseable bucket.` : ''
  } **${dist.senior}** look senior, **${dist.mid}** look mid-level${
    dist.junior > 0 ? `, and **${dist.junior}** are junior` : ''
  }.`;

  const jdSentence = contextJd
    ? ` The JD asks for ${signalKw ? `**${signalKw}**` : 'your listed criteria'}.`
    : ' No role context was pasted — I can score against generic relevance, or pause until you give me a JD.';

  const question = contextJd
    ? `Should I filter out the **${dist.junior}** sub-2y candidates up front, or keep them in so you can see the full distribution?`
    : 'Want to paste a JD now, or should I proceed with a generic seniority scan?';

  return {
    paraphrase: `${countSentence}${jdSentence}\n\n${question}`,
    thoughtMs: 1400 + Math.floor(Math.random() * 1200),
  };
}

/**
 * §29.F manual mode — short summary only, no question.
 */
export function buildBulkResumeManualSummary(
  resumes: SmartMatchResumeEntry[],
): ClarificationRound {
  const dist = stubSeniorityDistribution(resumes);
  return {
    paraphrase: `Ready — **${resumes.length - dist.unparseable}** résumés parsed${
      dist.unparseable > 0 ? `, **${dist.unparseable}** unparseable` : ''
    }. Press Start scoring when you're ready.`,
    thoughtMs: 800,
  };
}

// ── §9.2 LLM-backed paraphrase ─────────────────────────────────────────────
//
// When `VITE_SMART_MATCH_CLARIFY_LLM=1` is set, the Clarification Stage
// asks the real LLM (Groq primary, Ollama fallback via `invokeLlamaChat`)
// to produce the paraphrase instead of the keyword stub. On *any* failure
// these helpers silently fall back to the stub — Stage 1 must never block.

const CLARIFY_SYSTEM_PROMPT = `You are a recruiting-search assistant helping a recruiter find candidates fast.

Given the recruiter's input, respond with ONE or TWO paragraphs:

Paragraph 1 (required) — a one-sentence confirmation that you understood the brief. **Bold** the 2–5 most important filter signals (skills, seniority, location, years-of-experience). Use Markdown **bold** syntax.

Paragraph 2 (optional) — add ONLY if critical information is genuinely missing (e.g. no role, no location at all, completely vague). If the brief is specific enough to start a search, SKIP paragraph 2 entirely. Do NOT ask questions for the sake of it.

Rules:
- If the brief has a role title, experience level, OR location → output ONE paragraph only.
- Never greet, never apologise, never restate these rules.
- Never use bullet points, headings, or code fences.
- Plain prose with inline **bold** only.
- Bias strongly toward confirming and starting — recruiters want results, not questions.`;

const CLARIFY_FOLLOWUP_SYSTEM_PROMPT = `You are a recruiting-search assistant. The recruiter sent an original brief and then a refinement.

Respond with ONE paragraph only:
- Acknowledge the refinement in one sentence, restate the updated filter set with **bolded** keywords.
- Do NOT ask any further questions. The recruiter is ready to start — confirm you are too.

Plain prose, inline **bold**, no bullets, no greetings, one paragraph maximum.`;

function llmClarifyEnabled(): boolean {
  return import.meta.env.VITE_SMART_MATCH_CLARIFY_LLM === '1' ||
         import.meta.env.VITE_SMART_MATCH_CLARIFY_LLM === 'true';
}

interface LlmMeta {
  orgId?: string | null;
  orgUserId?: string | null;
}

async function callClarifyLlm(
  messages: LlamaChatMessage[],
  meta?: LlmMeta,
): Promise<string | null> {
  try {
    const res = await invokeLlamaChat(
      messages,
      undefined,
      meta?.orgId
        ? { orgId: meta.orgId, feature: 'smart_match_clarify', orgUserId: meta.orgUserId ?? null }
        : null,
      null,
    );
    const out = (res.content ?? '').trim();
    return out.length > 0 ? out : null;
  } catch (err) {
    console.warn(
      '[paraphraseStub] LLM clarify failed; falling back to stub:',
      err instanceof Error ? err.message : err,
    );
    return null;
  }
}

/**
 * LLM-backed initial paraphrase. Falls back to `buildInitialParaphrase`
 * on any failure or when the LLM clarify flag is off.
 */
export async function buildInitialParaphraseLlm(
  prompt: string,
  meta?: LlmMeta,
): Promise<ClarificationRound> {
  if (!llmClarifyEnabled()) return buildInitialParaphrase(prompt);
  const startedAt = performance.now();
  const content = await callClarifyLlm(
    [
      { role: 'system', content: CLARIFY_SYSTEM_PROMPT },
      { role: 'user', content: prompt },
    ],
    meta,
  );
  if (!content) return buildInitialParaphrase(prompt);
  return {
    paraphrase: content,
    thoughtMs: Math.max(600, Math.round(performance.now() - startedAt)),
  };
}

/**
 * LLM-backed follow-up paraphrase. Falls back to `buildFollowUpParaphrase`
 * on any failure or when the LLM clarify flag is off.
 */
export async function buildFollowUpParaphraseLlm(
  originalPrompt: string,
  refinement: string,
  round: number,
  meta?: LlmMeta,
): Promise<ClarificationRound> {
  if (!llmClarifyEnabled()) {
    return buildFollowUpParaphrase(originalPrompt, refinement, round);
  }
  const startedAt = performance.now();
  const content = await callClarifyLlm(
    [
      { role: 'system', content: CLARIFY_FOLLOWUP_SYSTEM_PROMPT },
      { role: 'user', content: `Original brief:\n${originalPrompt}` },
      { role: 'user', content: `Refinement (round ${round}):\n${refinement}` },
    ],
    meta,
  );
  if (!content) return buildFollowUpParaphrase(originalPrompt, refinement, round);
  return {
    paraphrase: content,
    thoughtMs: Math.max(600, Math.round(performance.now() - startedAt)),
  };
}
